import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, ExternalLink, Trash2, Eye, Lock, Globe, Copy } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function Wishlists() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/wishlists/mine");
        setLists(data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const remove = async (id) => {
    if (!window.confirm("Delete this wishlist?")) return;
    try {
      await api.delete(`/wishlists/${id}`);
      setLists((l) => l.filter((x) => x.id !== id));
      toast.success("Wishlist deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = async (slug) => {
    const apiBase = api.defaults.baseURL || "";
    const url = `${apiBase}/share/wishlist/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied.");
    } catch (e) { toast.error("Couldn't copy."); }
  };

  const togglePublic = async (wl) => {
    try {
      const { data } = await api.patch(`/wishlists/${wl.id}`, { is_public: !wl.is_public });
      setLists((all) => all.map((x) => x.id === wl.id ? { ...x, ...data } : x));
      toast.success(data.is_public ? "Wishlist is now public." : "Wishlist is now private.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (authLoading || !user) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="wishlists-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]" data-testid="wishlists-back-link">
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to home
      </Link>

      <div className="mt-6 mb-10 border-b border-black/10 pb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2">Wishlist Bundles</div>
          <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter">My wishlists</h1>
          <p className="mt-3 text-black/60 font-serif italic max-w-2xl">
            Bundle any group of saved deals into a public link that friends can browse — no signup required for them.
          </p>
        </div>
        <Link
          to="/bookmarks"
          className="inline-flex items-center gap-2 bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]"
          data-testid="wishlists-create-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          Create from bookmarks
        </Link>
      </div>

      {loading ? (
        <div className="text-xs font-mono uppercase tracking-widest text-black/40">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="wishlists-empty">
          <div className="font-serif text-3xl mb-2">No wishlists yet</div>
          <p className="text-black/60 text-sm mb-6 max-w-md mx-auto">
            Head to your bookmarks, pick a few deals, and bundle them into a shareable list.
          </p>
          <Link to="/bookmarks" className="inline-block bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]">
            Open bookmarks
          </Link>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="wishlists-list">
          {lists.map((w) => (
            <li key={w.id} className="border-2 border-black bg-white p-5" data-testid={`wishlist-row-${w.slug}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">
                    {w.is_public ? (<><Globe className="w-3 h-3" strokeWidth={2} /> Public</>) : (<><Lock className="w-3 h-3" strokeWidth={2} /> Private</>)}
                    <span className="w-1 h-1 bg-black/30 rounded-full" />
                    <span>{w.deal_ids?.length || 0} deals</span>
                    <span className="w-1 h-1 bg-black/30 rounded-full" />
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" strokeWidth={2} />{w.view_count || 0} views</span>
                  </div>
                  <Link to={`/w/${w.slug}`} className="font-serif text-2xl font-black tracking-tight hover:text-[#E63946]" data-testid={`wishlist-name-${w.slug}`}>
                    {w.name}
                  </Link>
                  {w.description && <p className="text-sm text-black/60 mt-1 font-serif">{w.description}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => togglePublic(w)}
                    className="inline-flex items-center gap-1.5 border border-black/20 text-[10px] font-bold uppercase tracking-widest px-2.5 py-2 hover:border-black"
                    data-testid={`wishlist-toggle-public-${w.slug}`}
                  >
                    {w.is_public ? <Lock className="w-3 h-3" strokeWidth={2} /> : <Globe className="w-3 h-3" strokeWidth={2} />}
                    {w.is_public ? "Make private" : "Make public"}
                  </button>
                  {w.is_public && (
                    <button
                      onClick={() => copy(w.slug)}
                      className="inline-flex items-center gap-1.5 border border-black/20 text-[10px] font-bold uppercase tracking-widest px-2.5 py-2 hover:border-black"
                      data-testid={`wishlist-copy-${w.slug}`}
                    >
                      <Copy className="w-3 h-3" strokeWidth={2} />
                      Copy link
                    </button>
                  )}
                  <Link
                    to={`/w/${w.slug}`}
                    className="inline-flex items-center gap-1.5 bg-black text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-[#E63946]"
                    data-testid={`wishlist-open-${w.slug}`}
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={2} />
                    Open
                  </Link>
                  <button
                    onClick={() => remove(w.id)}
                    className="inline-flex items-center gap-1.5 border border-black/20 text-black/50 text-[10px] font-bold uppercase tracking-widest px-2.5 py-2 hover:border-[#E63946] hover:text-[#E63946]"
                    data-testid={`wishlist-delete-${w.slug}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
